import React, { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, MessageSquare, Package, Globe, Watch, Droplet } from 'lucide-react';
import { SECTION_RULES } from '../../utils/cartRules';
import { slugifySection } from '../../utils/sectionUtils';
import CartItemRow from './CartItemRow';

// Icon mapper helper
const getSectionIcon = (sectionName) => {
  switch (sectionName) {
    case 'National Articles':
      return Package;
    case 'Premium & Imported':
      return Globe;
    case 'Accessories':
      return Watch;
    case 'Fragrance & Grooming':
      return Droplet;
    default:
      return AlertTriangle;
  }
};

const CartSectionCard = memo(({ 
  sectionName, 
  sectionIndex, 
  items, 
  note, 
  onUpdateNote, 
  onUpdateQty, 
  onRemove, 
  sectionConfig,
  productMOQs,
  inFlightUpdates
}) => {
  // Session storage persistence for collapse state
  const collapseKey = `cart_section_collapsed_${sectionName}`;
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return sessionStorage.getItem(collapseKey) === 'true';
  });

  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      sessionStorage.setItem(collapseKey, next);
      return next;
    });
  };

  // Section styling color fallback
  const sectionColor = sectionConfig?.color || (() => {
    switch (sectionName) {
      case 'National Articles': return '#10b981'; // Green
      case 'Premium & Imported': return '#3b82f6'; // Blue
      case 'Accessories': return '#8b5cf6'; // Purple
      case 'Fragrance & Grooming': return '#f97316'; // Orange
      default: return '#ef4444'; // Red for legacy/unknown
    }
  })();

  const SectionIcon = getSectionIcon(sectionName);

  // Retrieve rule from cartRules
  const rule = SECTION_RULES[sectionName] || { type: 'unknown' };

  // Calculate metrics
  const totalQty = items.reduce((acc, item) => acc + item.qty, 0);
  const totalValue = items.reduce((acc, item) => acc + (item.price * item.qty), 0);

  let isEligible = false;
  let statusBadgeText = '';
  let progressPercent = 0;
  let progressText = '';
  let warningMessage = '';
  let moqBadgeText = '';

  if (rule.type === 'none') {
    isEligible = true;
    statusBadgeText = '✓ No Minimum Requirement';
    progressPercent = 100;
    moqBadgeText = 'No MOQ Requirement';
  } else if (rule.type === 'moq') {
    const limit = rule.value;
    isEligible = totalQty >= limit;
    progressPercent = Math.min(100, (totalQty / limit) * 100);
    moqBadgeText = `MOQ: ${limit} Pcs`;
    progressText = `${totalQty} / ${limit} Pcs`;
    
    if (isEligible) {
      statusBadgeText = '✓ MOQ Met';
    } else {
      const needed = limit - totalQty;
      statusBadgeText = `⚠ Need ${needed} More Pcs`;
      warningMessage = `Add ${needed} more pcs to reach the MOQ of ${limit} pcs.`;
    }
  } else if (rule.type === 'value') {
    const limit = rule.value;
    isEligible = totalValue >= limit;
    progressPercent = Math.min(100, (totalValue / limit) * 100);
    moqBadgeText = `Min. Order: ₹${limit}`;
    progressText = `₹${totalValue} / ₹${limit}`;

    if (isEligible) {
      statusBadgeText = '✓ Eligible';
    } else {
      const needed = limit - totalValue;
      statusBadgeText = `⚠ Need ₹${needed} More`;
      warningMessage = `Add ₹${needed} more to reach the minimum order value of ₹${limit}.`;
    }
  } else {
    // Unknown / legacy items
    isEligible = false;
    statusBadgeText = '⚠ Legacy Section';
    progressPercent = 0;
    moqBadgeText = 'Invalid Section';
    warningMessage = 'Legacy items detected. Please remove and re-add to apply MOQ validation.';
  }

  const cardStyle = {
    '--section-color': sectionColor,
    '--section-color-glow': `${sectionColor}15`
  };

  return (
    <div className={`section-card-panel ${isCollapsed ? 'collapsed' : ''}`} style={cardStyle}>
      {/* Header Bar */}
      <div className="section-card-header" onClick={toggleCollapse}>
        <div className="header-left">
          <div className="section-icon-badge">
            <SectionIcon size={18} />
          </div>
          <h3 className="section-title">
            <span className="section-number">{sectionIndex}. </span>
            {sectionName}
          </h3>
          <span className="moq-badge">{moqBadgeText}</span>
        </div>

        <div className="header-right">
          {rule.type !== 'none' && rule.type !== 'unknown' && (
            <div className="section-header-progress-wrapper">
              <div className="header-progress-bar-bg">
                <div 
                  className="header-progress-bar-fill" 
                  style={{ width: `${progressPercent}%`, backgroundColor: isEligible ? '#10b981' : sectionColor }}
                />
              </div>
              <span className="header-progress-text">{progressText}</span>
            </div>
          )}
          
          <span className={`status-badge-inline ${isEligible ? 'success' : 'warning'}`}>
            {isEligible ? (
              <CheckCircle2 size={14} className="status-badge-icon" />
            ) : (
              <AlertTriangle size={14} className="status-badge-icon" />
            )}
            {statusBadgeText}
          </span>
          
          <span className="collapse-toggle-btn">
            {isCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
          </span>
        </div>
      </div>

      {/* Body List & Controls */}
      {!isCollapsed && (
        <div className="section-card-body">
          <div className="table-responsive">
            <table className="cart-products-table">
              <thead>
                <tr>
                  <th className="col-product">Product</th>
                  <th className="col-price">Price</th>
                  <th className="col-quantity">Quantity</th>
                  <th className="col-total">Total</th>
                  <th className="col-remove"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <CartItemRow
                    key={item.productId}
                    item={item}
                    onUpdateQty={onUpdateQty}
                    onRemove={onRemove}
                    sectionColor={sectionColor}
                    minMoq={productMOQs?.[item.productId] || 1}
                    isUpdating={!!inFlightUpdates?.[item.productId]}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Validation Warning Alert */}
          {!isEligible && warningMessage && (
            <div className="section-warning-banner">
              <AlertTriangle size={16} className="warning-banner-icon" />
              <p className="warning-banner-text">{warningMessage}</p>
            </div>
          )}

          {/* Footer controls: Notes + Navigation */}
          <div className="section-card-footer">
            <div className="section-note-box">
              <MessageSquare size={16} className="note-box-icon" />
              <input
                type="text"
                value={note || ''}
                onChange={(e) => onUpdateNote(e.target.value)}
                placeholder="Add note about size, color, material or any other specification..."
                className="note-box-input"
              />
            </div>

            {sectionName !== 'Unknown Section' && (
              <Link 
                to={`/section/${slugifySection(sectionName)}`}
                className="btn-continue-shopping"
              >
                Continue Shopping &rarr;
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default CartSectionCard;
